import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { 
    Card, 
    Spinner, 
    Badge, 
    Alert, 
    Button, 
    Table, 
    Tabs, 
    Tab,
    Row,
    Col
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API_URL } from "../../utils/constant";
import { 
    faGlobe, 
    faShieldAlt, 
    faNetworkWired, 
    faInfoCircle,
    faBug,
    faFlag,
    faTag,
    faClock,
    faArrowLeft,
    faExternalLinkAlt,
    faExclamationTriangle,
    faCode,
    faLink,
    faChevronDown,
    faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

const ThreatActorDetails = () => {
    const { id } = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [threatActor, setThreatActor] = useState(null);
    
    // Tab states
    const [activeTab, setActiveTab] = useState('overview');
    const [cves, setCves] = useState([]);
    const [cvesLoading, setCvesLoading] = useState(false);
    const [ttps, setTtps] = useState([]);
    const [ttpsLoading, setTtpsLoading] = useState(false);
    const [ttpsCursor, setTtpsCursor] = useState(null);
    const [ttpsHasMore, setTtpsHasMore] = useState(false);
    const [malware, setMalware] = useState([]);
    const [malwareLoading, setMalwareLoading] = useState(false);
    const [malwareCursor, setMalwareCursor] = useState(null);
    const [malwareHasMore, setMalwareHasMore] = useState(false);
    const [iocs, setIocs] = useState([]);
    const [iocsLoading, setIocsLoading] = useState(false);
    const [iocsCursor, setIocsCursor] = useState(null);
    const [iocsHasMore, setIocsHasMore] = useState(false);
    
    const [copiedField, setCopiedField] = useState(null);
    const [expandedTtpDescriptions, setExpandedTtpDescriptions] = useState(new Set());
    const [expandedMalwareDescriptions, setExpandedMalwareDescriptions] = useState(new Set());

    // Fetch threat actor details
    const fetchThreatActorDetails = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch(`${API_URL}/threat-intel/threat-actors/${id}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.error) {
                throw new Error(data.userMessage || 'Failed to fetch threat actor details');
            }

            setThreatActor(data.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch CVEs
    const fetchCVEs = async () => {
        setCvesLoading(true);
        try {
            const response = await fetch(`${API_URL}/threat-intel/threat-actors/${id}/cves?count=200`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            if (data.error) {
                throw new Error(data.userMessage || 'Failed to fetch CVEs');
            }
            setCves(data.data?.cves || []);
        } catch (err) {
            console.error('Error fetching CVEs:', err);
        } finally {
            setCvesLoading(false);
        }
    };

    // Fetch TTPs
    const fetchTTPs = async (cursor = null) => {
        setTtpsLoading(true);
        try {
            let url = `${API_URL}/threat-intel/threat-actors/${id}/ttps?count=100`;
            if (cursor) {
                url += `&cursor=${encodeURIComponent(cursor)}`;
            }

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            if (data.error) {
                throw new Error(data.userMessage || 'Failed to fetch TTPs');
            }
            
            // Extract attack patterns from results array
            const newTtps = (data.data?.results || []).map(result => ({
                ...result.attackPattern,
                relationship_type: result.relationship?.relationship_type,
                relationship_created_at: result.relationship?.created_at
            }));
            
            setTtps(prev => cursor ? [...prev, ...newTtps] : newTtps);
            setTtpsCursor(data.data?.pagination?.endCursor);
            setTtpsHasMore(data.data?.pagination?.hasNextPage || false);
        } catch (err) {
            console.error('Error fetching TTPs:', err);
        } finally {
            setTtpsLoading(false);
        }
    };

    // Fetch Malware
    const fetchMalware = async (cursor = null) => {
        setMalwareLoading(true);
        try {
            let url = `${API_URL}/threat-intel/threat-actors/${id}/malware?count=100`;
            if (cursor) {
                url += `&cursor=${encodeURIComponent(cursor)}`;
            }

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            if (data.error) {
                throw new Error(data.userMessage || 'Failed to fetch malware');
            }
            
            // Extract malware from stixCoreObjects.edges
            const edges = data.data?.stixCoreObjects?.edges || [];
            const newMalware = edges.map(edge => edge.node);
            
            setMalware(prev => cursor ? [...prev, ...newMalware] : newMalware);
            setMalwareCursor(data.data?.stixCoreObjects?.pageInfo?.endCursor);
            setMalwareHasMore(data.data?.stixCoreObjects?.pageInfo?.hasNextPage || false);
        } catch (err) {
            console.error('Error fetching malware:', err);
        } finally {
            setMalwareLoading(false);
        }
    };

    // Fetch IOCs
    const fetchIOCs = async (cursor = null) => {
        setIocsLoading(true);
        try {
            let url = `${API_URL}/threat-intel/threat-actors/${id}/iocs?count=100`;
            if (cursor) {
                url += `&cursor=${encodeURIComponent(cursor)}`;
            }

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            if (data.error) {
                throw new Error(data.userMessage || 'Failed to fetch IOCs');
            }
            
            const newIocs = data.data?.indicators || [];
            setIocs(prev => cursor ? [...prev, ...newIocs] : newIocs);
            setIocsCursor(data.data?.pagination?.endCursor);
            setIocsHasMore(data.data?.pagination?.hasNextPage || false);
        } catch (err) {
            console.error('Error fetching IOCs:', err);
        } finally {
            setIocsLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchThreatActorDetails();
        }
    }, [id]);

    useEffect(() => {
        if (activeTab === 'cves' && cves.length === 0 && !cvesLoading) {
            fetchCVEs();
        } else if (activeTab === 'ttps' && ttps.length === 0 && !ttpsLoading) {
            fetchTTPs();
        } else if (activeTab === 'malware' && malware.length === 0 && !malwareLoading) {
            fetchMalware();
        } else if (activeTab === 'iocs' && iocs.length === 0 && !iocsLoading) {
            fetchIOCs();
        }
    }, [activeTab]);

    const handleCopy = (text, fieldName) => {
        navigator.clipboard.writeText(text);
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const toggleTtpDescription = (ttpIndex) => {
        setExpandedTtpDescriptions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(ttpIndex)) {
                newSet.delete(ttpIndex);
            } else {
                newSet.add(ttpIndex);
            }
            return newSet;
        });
    };

    const toggleMalwareDescription = (malwareIndex) => {
        setExpandedMalwareDescriptions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(malwareIndex)) {
                newSet.delete(malwareIndex);
            } else {
                newSet.add(malwareIndex);
            }
            return newSet;
        });
    };

    const getConfidenceColor = (confidence) => {
        if (confidence >= 80) return '#dc3545';
        if (confidence >= 50) return '#ffc107';
        if (confidence >= 20) return '#0d6efd';
        return '#6c757d';
    };

    const getConfidenceLabel = (confidence) => {
        if (confidence >= 80) return 'High';
        if (confidence >= 50) return 'Medium';
        if (confidence >= 20) return 'Low';
        return 'Unknown';
    };

    const getResourceLevelColor = (level) => {
        switch (level?.toLowerCase()) {
            case 'government': return 'danger';
            case 'individual': return 'warning';
            case 'organization': return 'info';
            default: return 'secondary';
        }
    };

    const getMotivationColor = (motivation) => {
        switch (motivation?.toLowerCase()) {
            case 'organizational-gain': return 'primary';
            case 'personal-gain': return 'warning';
            case 'ideology': return 'danger';
            case 'dominance': return 'dark';
            case 'cyber-espionage': return 'info';
            default: return 'secondary';
        }
    };

    const extractCountries = (targetsCountries) => {
        if (!targetsCountries?.edges || targetsCountries.edges.length === 0) return [];
        return targetsCountries.edges.map(edge => edge.node?.to?.name).filter(Boolean);
    };

    const extractSectors = (targetsSectors) => {
        if (!targetsSectors?.edges || targetsSectors.edges.length === 0) return [];
        return targetsSectors.edges.map(edge => edge.node?.to?.name).filter(Boolean);
    };

    const extractMalware = (usesMalware) => {
        if (!usesMalware?.edges || usesMalware.edges.length === 0) return [];
        return usesMalware.edges.map(edge => ({
            name: edge.node?.to?.name,
            types: edge.node?.to?.malware_types
        })).filter(item => item.name);
    };

    const extractOrigins = (originatesFrom) => {
        if (!originatesFrom?.edges || originatesFrom.edges.length === 0) return [];
        return originatesFrom.edges.map(edge => edge.node?.to?.name).filter(Boolean);
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="content-wrapper">
                    <div className="text-center my-5 py-5">
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <p className="mt-3 text-muted">Loading threat actor details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid">
                <div className="content-wrapper">
                    <Alert variant="danger" className="mb-4">
                        <Alert.Heading>Error</Alert.Heading>
                        <p>{error}</p>
                        <Button variant="outline-danger" onClick={() => history.goBack()}>
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                            Go Back
                        </Button>
                    </Alert>
                </div>
            </div>
        );
    }

    if (!threatActor) {
        return (
            <div className="container-fluid">
                <div className="content-wrapper">
                    <Alert variant="warning" className="mb-4">
                        <Alert.Heading>Not Found</Alert.Heading>
                        <p>Threat actor not found.</p>
                        <Button variant="outline-warning" onClick={() => history.goBack()}>
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                            Go Back
                        </Button>
                    </Alert>
                </div>
            </div>
        );
    }

    const confidence = threatActor.confidence || 0;
    const confidenceColor = getConfidenceColor(confidence);
    // Use new field names from API response
    const countries = extractCountries(threatActor.targetedCountries || threatActor.targetsCountries);
    const sectors = extractSectors(threatActor.targetedSectors || threatActor.targetsSectors);
    const overviewMalware = extractMalware(threatActor.usesMalware);
    const origins = extractOrigins(threatActor.originatesFrom);

    return (
        <div className="container-fluid">
            <div className="content-wrapper">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => history.goBack()}
                            className="mb-3"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                            Back to Threat Actors
                        </Button>
                        <div className="block-header">
                            <h2 className="welcome-title">{threatActor.name}</h2>
                            <p className="welcome-textSmall">Threat Actor Profile & Intelligence</p>
                        </div>
                    </div>
                    {/* <div className="text-center">
                        <div 
                            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                            style={{ 
                                width: '80px', 
                                height: '80px', 
                                backgroundColor: `${confidenceColor}15`,
                                color: confidenceColor,
                                fontWeight: 'bold',
                                fontSize: '1.2rem',
                                border: `3px solid ${confidenceColor}40`
                            }}
                        >
                            {confidence}%
                        </div>
                        <div className="small fw-semibold" style={{ color: confidenceColor }}>
                            {getConfidenceLabel(confidence)} Confidence
                        </div>
                    </div> */}
                </div>

                {/* Tabs */}
                <Card className="shadow-sm" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                    <style>{`
                        .threat-actor-tabs .nav-tabs {
                            border-bottom: none !important;
                        }
                        .threat-actor-tabs .nav-tabs .nav-link {
                            color: rgba(255, 255, 255, 0.7) !important;
                            background-color: transparent !important;
                            border: none !important;
                            border-bottom: 2px solid transparent !important;
                            padding: 12px 20px !important;
                        }
                        .threat-actor-tabs .nav-tabs .nav-link:hover {
                            color: white !important;
                            background-color: rgba(255, 255, 255, 0.1) !important;
                            border-bottom: 2px solid rgba(255, 255, 255, 0.3) !important;
                        }
                        .threat-actor-tabs .nav-tabs .nav-link.active {
                            color: white !important;
                            background-color: rgba(255, 255, 255, 0.15) !important;
                            border-bottom: 2px solid white !important;
                        }
                    `}</style>
                    <Card.Header style={{ 
                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', 
                        color: 'white',
                        padding: '0',
                        borderBottom: 'none'
                    }}>
                        <Tabs
                            activeKey={activeTab}
                            onSelect={(k) => setActiveTab(k)}
                            className="border-0 threat-actor-tabs"
                            style={{ borderBottom: 'none' }}
                        >
                            <Tab 
                                eventKey="overview" 
                                title={
                                    <span >
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                        Overview
                                    </span>
                                }
                            />
                            <Tab 
                                eventKey="cves" 
                                title={
                                    <span >
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                        CVEs ({cves.length > 0 ? cves.length : '...'})
                                    </span>
                                }
                            />
                            <Tab 
                                eventKey="ttps" 
                                title={
                                    <span >
                                        <FontAwesomeIcon icon={faCode} className="me-2" />
                                        TTPs ({ttps.length > 0 ? ttps.length : '...'})
                                    </span>
                                }
                            />
                            <Tab 
                                eventKey="malware" 
                                title={
                                    <span >
                                        <FontAwesomeIcon icon={faBug} className="me-2" />
                                        Malware ({malware.length > 0 ? malware.length : '...'})
                                    </span>
                                }
                            />
                            <Tab 
                                eventKey="iocs" 
                                title={
                                    <span >
                                        <FontAwesomeIcon icon={faFlag} className="me-2" />
                                        IOCs ({iocs.length > 0 ? iocs.length : '...'})
                                    </span>
                                }
                            />
                        </Tabs>
                    </Card.Header>
                    <Card.Body className="p-4">
                        {/* Overview Tab */}
                        {activeTab === 'overview' && (
                            <div>
                                {/* Basic Information */}
                                <Row className="mb-4">
                                    <Col md={8}>
                                        <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                            <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                                <h5 className="mb-0 fw-bold">
                                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-primary" />
                                                    Basic Information
                                                </h5>
                                            </Card.Header>
                                            <Card.Body>
                                                {threatActor.aliases && threatActor.aliases.length > 0 && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Aliases:</strong>
                                                        <div className="mt-1">
                                                            {threatActor.aliases.map((alias, idx) => (
                                                                <Badge key={idx} bg="secondary" className="me-2 mb-2">
                                                                    {alias}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {threatActor.description && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Description:</strong>
                                                        <p className="mt-1 mb-0" style={{ lineHeight: '1.6' }}>
                                                            {threatActor.description.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')}
                                                        </p>
                                                    </div>
                                                )}
                                                {/* <Row>
                                                    <Col md={6}>
                                                        <div className="mb-3">
                                                            <strong className="text-muted small">Standard ID:</strong>
                                                            <div className="d-flex align-items-center mt-1">
                                                                <code className="small">{threatActor.standard_id || 'N/A'}</code>
                                                                {threatActor.standard_id && (
                                                                    <Button
                                                                        variant="link"
                                                                        size="sm"
                                                                        className="p-0 ms-2"
                                                                        onClick={() => handleCopy(threatActor.standard_id, 'standard_id')}
                                                                        title="Copy to clipboard"
                                                                    >
                                                                        <FontAwesomeIcon 
                                                                            icon={copiedField === 'standard_id' ? faCheckCircle : faCopy} 
                                                                            className={copiedField === 'standard_id' ? 'text-success' : 'text-muted'}
                                                                        />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="mb-3">
                                                            <strong className="text-muted small">Entity Type:</strong>
                                                            <div className="mt-1">{threatActor.entity_type || 'N/A'}</div>
                                                        </div>
                                                    </Col>
                                                </Row> */}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={4}>
                                        <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                            <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                                <h5 className="mb-0 fw-bold">
                                                    <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
                                                    Attributes
                                                </h5>
                                            </Card.Header>
                                            <Card.Body>
                                                {threatActor.resource_level && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Resource Level:</strong>
                                                        <div className="mt-1">
                                                            <Badge bg={getResourceLevelColor(threatActor.resource_level)}>
                                                                {threatActor.resource_level}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                )}
                                                {threatActor.primary_motivation && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Primary Motivation:</strong>
                                                        <div className="mt-1">
                                                            <Badge bg={getMotivationColor(threatActor.primary_motivation)}>
                                                                {threatActor.primary_motivation.replace(/-/g, ' ')}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                )}
                                                {threatActor.secondary_motivations && threatActor.secondary_motivations.length > 0 && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Secondary Motivations:</strong>
                                                        <div className="mt-1 d-flex flex-wrap gap-1">
                                                            {threatActor.secondary_motivations.map((motivation, idx) => (
                                                                <Badge key={idx} bg={getMotivationColor(motivation)}>
                                                                    {motivation.replace(/-/g, ' ')}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {threatActor.first_seen && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">First Seen:</strong>
                                                        <div className="mt-1">
                                                            <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                                                            {moment(threatActor.first_seen).format('MMM DD, YYYY')}
                                                        </div>
                                                    </div>
                                                )}
                                                {threatActor.last_seen && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Last Seen:</strong>
                                                        <div className="mt-1">
                                                            <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                                                            {moment(threatActor.last_seen).format('MMM DD, YYYY')}
                                                        </div>
                                                    </div>
                                                )}
                                                {threatActor.created_at && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Created:</strong>
                                                        <div className="mt-1">
                                                            <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                                                            {moment(threatActor.created_at).format('MMM DD, YYYY')}
                                                        </div>
                                                    </div>
                                                )}
                                                {threatActor.updated_at && (
                                                    <div className="mb-3">
                                                        <strong className="text-muted small">Updated:</strong>
                                                        <div className="mt-1">
                                                            <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                                                            {moment(threatActor.updated_at).format('MMM DD, YYYY')}
                                                        </div>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Labels and Markings */}
                                {threatActor.objectLabel && threatActor.objectLabel.length > 0 && (
                                    <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                        <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <h5 className="mb-0 fw-bold">
                                                <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
                                                Labels
                                            </h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="d-flex flex-wrap gap-2">
                                                {threatActor.objectLabel.map((label, idx) => (
                                                    <Badge 
                                                        key={idx}
                                                        style={{ 
                                                            backgroundColor: label.color || '#6c757d',
                                                            color: '#FFFFFF',
                                                            fontSize: '0.85rem',
                                                            padding: '6px 12px',
                                                            borderRadius: '6px'
                                                        }}
                                                    >
                                                        {label.value}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* Geographic Information */}
                                {(origins.length > 0 || countries.length > 0) && (
                                    <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                        <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <h5 className="mb-0 fw-bold">
                                                <FontAwesomeIcon icon={faGlobe} className="me-2 text-primary" />
                                                Geographic Information
                                            </h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                {origins.length > 0 && (
                                                    <Col md={6}>
                                                        <strong className="text-muted small">Originates From:</strong>
                                                        <div className="mt-2 d-flex flex-wrap gap-2">
                                                            {origins.map((origin, idx) => (
                                                                <Badge key={idx} bg="info" style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
                                                                    {origin}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </Col>
                                                )}
                                                {countries.length > 0 && (
                                                    <Col md={6}>
                                                        <strong className="text-muted small">Target Countries:</strong>
                                                        <div className="mt-2 d-flex flex-wrap gap-2">
                                                            {countries.map((country, idx) => (
                                                                <Badge key={idx} bg="warning" text="dark" style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
                                                                    {country}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </Col>
                                                )}
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* Target Sectors */}
                                {sectors.length > 0 && (
                                    <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                        <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <h5 className="mb-0 fw-bold">
                                                <FontAwesomeIcon icon={faNetworkWired} className="me-2 text-primary" />
                                                Target Sectors
                                            </h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="d-flex flex-wrap gap-2">
                                                {sectors.map((sector, idx) => (
                                                    <Badge key={idx} bg="warning" text="dark" style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
                                                        {sector}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* Associated Malware (from overview) */}
                                {overviewMalware.length > 0 && (
                                    <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                        <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <h5 className="mb-0 fw-bold">
                                                <FontAwesomeIcon icon={faBug} className="me-2 text-danger" />
                                                Associated Malware ({overviewMalware.length})
                                            </h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="d-flex flex-wrap gap-2">
                                                {overviewMalware.map((mal, idx) => (
                                                    <Badge key={idx} bg="danger" style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
                                                        {mal.name}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* External References */}
                                {threatActor.externalReferences && threatActor.externalReferences.edges && threatActor.externalReferences.edges.length > 0 && (
                                    <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                        <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <h5 className="mb-0 fw-bold">
                                                <FontAwesomeIcon icon={faLink} className="me-2 text-primary" />
                                                External References
                                            </h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <Table striped bordered hover size="sm">
                                                <thead>
                                                    <tr>
                                                        <th>Source</th>
                                                        <th>URL</th>
                                                        <th>Description</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {threatActor.externalReferences.edges.map((edge, idx) => (
                                                        <tr key={idx}>
                                                            <td>{edge.node?.source_name || 'N/A'}</td>
                                                            <td>
                                                                {edge.node?.url ? (
                                                                    <a href={edge.node.url} target="_blank" rel="noopener noreferrer">
                                                                        {edge.node.url}
                                                                        <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-2" />
                                                                    </a>
                                                                ) : 'N/A'}
                                                            </td>
                                                            <td>{edge.node?.description || 'N/A'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* Security Coverage */}
                                {threatActor.securityCoverage && threatActor.securityCoverage.coverage_information && threatActor.securityCoverage.coverage_information.length > 0 && (
                                    <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                                        <Card.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <h5 className="mb-0 fw-bold">
                                                <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-primary" />
                                                Security Coverage
                                            </h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <Table striped bordered hover size="sm">
                                                <thead>
                                                    <tr>
                                                        <th>Coverage Name</th>
                                                        <th>Coverage Score</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {threatActor.securityCoverage.coverage_information.map((coverage, idx) => (
                                                        <tr key={idx}>
                                                            <td>{coverage.coverage_name || 'N/A'}</td>
                                                            <td>
                                                                <Badge bg={coverage.coverage_score >= 80 ? 'success' : coverage.coverage_score >= 50 ? 'warning' : 'secondary'}>
                                                                    {coverage.coverage_score || 0}%
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                )}
                            </div>
                        )}

                        {/* CVEs Tab */}
                        {activeTab === 'cves' && (
                            <div>
                                {cvesLoading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3 text-muted">Loading CVEs...</p>
                                    </div>
                                ) : cves.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-3" style={{ fontSize: '4rem', opacity: 0.5 }} />
                                        <h5 className="text-muted mb-2">No CVEs found</h5>
                                        <p className="text-muted">No Common Vulnerabilities and Exposures associated with this threat actor.</p>
                                    </div>
                                ) : (
                                    <div>
                                        <Table striped bordered hover responsive>
                                            <thead className="table-light">
                                                <tr>
                                                    <th>CVE Name</th>
                                                    <th>Relationship Type</th>
                                                    <th>Confidence</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {cves.map((cve, idx) => (
                                                    <tr key={idx}>
                                                        <td className="fw-semibold">{cve.vulnerability?.name || 'N/A'}</td>
                                                        <td>
                                                            <Badge bg="info">{cve.relationship_type || 'N/A'}</Badge>
                                                        </td>
                                                        <td>
                                                            <Badge bg={cve.confidence >= 80 ? 'danger' : cve.confidence >= 50 ? 'warning' : 'secondary'}>
                                                                {cve.confidence || 0}%
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* TTPs Tab */}
                        {activeTab === 'ttps' && (
                            <div>
                                {ttpsLoading && ttps.length === 0 ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3 text-muted">Loading TTPs...</p>
                                    </div>
                                ) : ttps.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-3" style={{ fontSize: '4rem', opacity: 0.5 }} />
                                        <h5 className="text-muted mb-2">No TTPs found</h5>
                                        <p className="text-muted">No Tactics, Techniques, and Procedures associated with this threat actor.</p>
                                    </div>
                                ) : (
                                    <div>
                                        <Table striped bordered hover responsive>
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Name</th>
                                                    <th>MITRE ID</th>
                                                    <th>Description</th>
                                                    <th>Aliases</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ttps.map((ttp, idx) => {
                                                    const isExpanded = expandedTtpDescriptions.has(idx);
                                                    const description = ttp.description || 'N/A';
                                                    const shouldShowToggle = description.length > 200;
                                                    const displayDescription = isExpanded || !shouldShowToggle 
                                                        ? description 
                                                        : description.substring(0, 200) + '...';
                                                    
                                                    return (
                                                        <tr key={idx}>
                                                            <td className="fw-semibold">{ttp.name || 'N/A'}</td>
                                                            <td>
                                                                {ttp.x_mitre_id ? (
                                                                    <Badge bg="">{ttp.x_mitre_id}</Badge>
                                                                ) : 'N/A'}
                                                            </td>
                                                            <td style={{ maxWidth: '500px' }}>
                                                                <div style={{ 
                                                                    lineHeight: '1.6',
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-word'
                                                                }}>
                                                                    {displayDescription}
                                                                </div>
                                                                {shouldShowToggle && (
                                                                    <Button
                                                                        variant="link"
                                                                        size="sm"
                                                                        className="p-0 mt-1"
                                                                        onClick={() => toggleTtpDescription(idx)}
                                                                        style={{ 
                                                                            textDecoration: 'none',
                                                                            fontSize: '0.85rem',
                                                                            color: '#0d6efd'
                                                                        }}
                                                                    >
                                                                        {isExpanded ? (
                                                                            <>
                                                                                Show less <FontAwesomeIcon icon={faChevronUp} className="ms-1" />
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                Show more <FontAwesomeIcon icon={faChevronDown} className="ms-1" />
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {ttp.aliases && Array.isArray(ttp.aliases) && ttp.aliases.length > 0 ? (
                                                                    <div className="d-flex flex-wrap gap-1">
                                                                        {ttp.aliases.slice(0, 3).map((alias, aliasIdx) => (
                                                                            <Badge key={aliasIdx} bg="secondary" style={{ fontSize: '0.75rem' }}>
                                                                                {alias}
                                                                            </Badge>
                                                                        ))}
                                                                        {ttp.aliases.length > 3 && (
                                                                            <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>
                                                                                +{ttp.aliases.length - 3}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                ) : 'N/A'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                        {ttpsHasMore && (
                                            <div className="text-center mt-3">
                                                <Button 
                                                    variant="outline-primary" 
                                                    onClick={() => fetchTTPs(ttpsCursor)}
                                                    disabled={ttpsLoading}
                                                >
                                                    {ttpsLoading ? (
                                                        <>
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            Loading...
                                                        </>
                                                    ) : (
                                                        'Load More TTPs'
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Malware Tab */}
                        {activeTab === 'malware' && (
                            <div>
                                {malwareLoading && malware.length === 0 ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3 text-muted">Loading malware...</p>
                                    </div>
                                ) : malware.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-3" style={{ fontSize: '4rem', opacity: 0.5 }} />
                                        <h5 className="text-muted mb-2">No malware found</h5>
                                        <p className="text-muted">No malware associated with this threat actor.</p>
                                    </div>
                                ) : (
                                    <div>
                                        <Table striped bordered hover responsive>
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Aliases</th>
                                                    <th>Description</th>
                                                    <th>Labels</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {malware.map((mal, idx) => {
                                                    const isExpanded = expandedMalwareDescriptions.has(idx);
                                                    const description = mal.description || 'N/A';
                                                    const shouldShowToggle = description !== 'N/A' && description.length > 200;
                                                    const displayDescription = isExpanded || !shouldShowToggle 
                                                        ? description 
                                                        : description.substring(0, 200) + '...';
                                                    
                                                    return (
                                                        <tr key={idx}>
                                                            <td className="fw-semibold">{mal.name || 'N/A'}</td>
                                                            <td>
                                                                {mal.aliases && Array.isArray(mal.aliases) && mal.aliases.length > 0 ? (
                                                                    <div className="d-flex flex-wrap gap-1">
                                                                        {mal.aliases.slice(0, 3).map((alias, aliasIdx) => (
                                                                            <Badge key={aliasIdx} bg="secondary" style={{ fontSize: '0.75rem' }}>
                                                                                {alias}
                                                                            </Badge>
                                                                        ))}
                                                                        {mal.aliases.length > 3 && (
                                                                            <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>
                                                                                +{mal.aliases.length - 3}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                ) : 'N/A'}
                                                            </td>
                                                            <td style={{ maxWidth: '500px' }}>
                                                                {description !== 'N/A' ? (
                                                                    <>
                                                                        <div style={{ 
                                                                            lineHeight: '1.6',
                                                                            whiteSpace: 'pre-wrap',
                                                                            wordBreak: 'break-word'
                                                                        }}>
                                                                            {displayDescription}
                                                                        </div>
                                                                        {shouldShowToggle && (
                                                                            <Button
                                                                                variant="link"
                                                                                size="sm"
                                                                                className="p-0 mt-1"
                                                                                onClick={() => toggleMalwareDescription(idx)}
                                                                                style={{ 
                                                                                    textDecoration: 'none',
                                                                                    fontSize: '0.85rem',
                                                                                    color: '#0d6efd'
                                                                                }}
                                                                            >
                                                                                {isExpanded ? (
                                                                                    <>
                                                                                        Show less <FontAwesomeIcon icon={faChevronUp} className="ms-1" />
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        Show more <FontAwesomeIcon icon={faChevronDown} className="ms-1" />
                                                                                    </>
                                                                                )}
                                                                            </Button>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <span className="text-muted">N/A</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {mal.objectLabel && Array.isArray(mal.objectLabel) && mal.objectLabel.length > 0 ? (
                                                                    <div className="d-flex flex-wrap gap-1">
                                                                        {mal.objectLabel.slice(0, 3).map((label, labelIdx) => (
                                                                            <Badge 
                                                                                key={labelIdx}
                                                                                style={{ 
                                                                                    backgroundColor: label.color || '#6c757d',
                                                                                    color: '#FFFFFF',
                                                                                    fontSize: '0.75rem'
                                                                                }}
                                                                            >
                                                                                {label.value}
                                                                            </Badge>
                                                                        ))}
                                                                        {mal.objectLabel.length > 3 && (
                                                                            <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>
                                                                                +{mal.objectLabel.length - 3}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                ) : 'N/A'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                        {malwareHasMore && (
                                            <div className="text-center mt-3">
                                                <Button 
                                                    variant="outline-primary" 
                                                    onClick={() => fetchMalware(malwareCursor)}
                                                    disabled={malwareLoading}
                                                >
                                                    {malwareLoading ? (
                                                        <>
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            Loading...
                                                        </>
                                                    ) : (
                                                        'Load More Malware'
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* IOCs Tab */}
                        {activeTab === 'iocs' && (
                            <div>
                                {iocsLoading && iocs.length === 0 ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3 text-muted">Loading IOCs...</p>
                                    </div>
                                ) : iocs.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-3" style={{ fontSize: '4rem', opacity: 0.5 }} />
                                        <h5 className="text-muted mb-2">No IOCs found</h5>
                                        <p className="text-muted">No Indicators of Compromise associated with this threat actor.</p>
                                    </div>
                                ) : (
                                    <div>
                                        <Table striped bordered hover responsive>
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Pattern Type</th>
                                                    <th>Observable Type</th>
                                                    <th>Score</th>
                                                    <th>Valid From</th>
                                                    <th>Valid Until</th>
                                                    <th>Markings</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {iocs.map((ioc, idx) => (
                                                    <tr key={idx}>
                                                        <td className="fw-semibold">{ioc.name || 'N/A'}</td>
                                                        <td>
                                                            <Badge bg="info">{ioc.pattern_type || 'N/A'}</Badge>
                                                        </td>
                                                        <td>
                                                            <Badge bg="secondary">{ioc.x_opencti_main_observable_type || 'N/A'}</Badge>
                                                        </td>
                                                        <td>
                                                            <Badge bg={ioc.x_opencti_score >= 80 ? 'danger' : ioc.x_opencti_score >= 50 ? 'warning' : 'secondary'}>
                                                                {ioc.x_opencti_score || 0}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            {ioc.valid_from ? moment(ioc.valid_from).format('MMM DD, YYYY') : 'N/A'}
                                                        </td>
                                                        <td>
                                                            {ioc.valid_until ? moment(ioc.valid_until).format('MMM DD, YYYY') : 'N/A'}
                                                        </td>
                                                        <td>
                                                            {ioc.objectMarking && ioc.objectMarking.length > 0 ? (
                                                                <div className="d-flex flex-wrap gap-1">
                                                                    {ioc.objectMarking.slice(0, 2).map((marking, markingIdx) => (
                                                                        <Badge 
                                                                            key={markingIdx}
                                                                            bg="light"
                                                                            text="dark"
                                                                            style={{ fontSize: '0.75rem' }}
                                                                        >
                                                                            {marking.definition_type || marking.definition || 'N/A'}
                                                                        </Badge>
                                                                    ))}
                                                                    {ioc.objectMarking.length > 2 && (
                                                                        <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>
                                                                            +{ioc.objectMarking.length - 2}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            ) : 'N/A'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        {iocsHasMore && (
                                            <div className="text-center mt-3">
                                                <Button 
                                                    variant="outline-primary" 
                                                    onClick={() => fetchIOCs(iocsCursor)}
                                                    disabled={iocsLoading}
                                                >
                                                    {iocsLoading ? (
                                                        <>
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            Loading...
                                                        </>
                                                    ) : (
                                                        'Load More IOCs'
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
};

export default ThreatActorDetails;
